import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActions, siteNavItems } from "@/app/content";
import { ALL_FAQS, FAQ_GROUPS } from "@/app/seo-faq";

const groupAccents: Record<string, string> = {
  "what-is": "#A3E635",
  "vs-others": "#8B5CF6",
  "opc-business": "#F97316",
  "trust-security": "#EF4444",
  "open-source": "#14B8A6",
  engineering: "#3B82F6",
  "marketplace-ops": "#FACC15",
};

export default function FaqPage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <ContentHero
        eyebrow="FAQ · 常见问题"
        eyebrowColor="#A3E635"
        titleTop="一次"
        titleBottom="解释清楚"
        bottomColor="#A3E635"
        lead="CALL ANYTHING 是一个让 AI Agent 通过标准化 Hotline 协议调用外部能力的开放体系。这页聚合了 OPC 商业化、与 MCP/Skill/API 的差异、安全信任、开源边界、协议接入、Marketplace 运营 7 个主题的高频问题。"
        meta={[`${ALL_FAQS.length} 条问答`, "整页 FAQPage schema", "为 LLM / Rich Results 优化"]}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "FAQ" },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <nav
          className="mb-10 border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          aria-label="FAQ 主题"
        >
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">主题目录</p>
          <ul className="mt-4 flex flex-wrap gap-3">
            {FAQ_GROUPS.map((group) => (
              <li key={group.id}>
                <a
                  href={`#group-${group.id}`}
                  className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 text-xs font-black uppercase text-black transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
                  style={{ boxShadow: `4px 4px 0 0 ${groupAccents[group.id] ?? "#A3E635"}` }}
                >
                  {group.label}
                  <span className="text-[10px] font-black text-zinc-500">{group.items.length}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-12">
          {FAQ_GROUPS.map((group) => {
            const accent = groupAccents[group.id] ?? "#A3E635";
            return (
              <section key={group.id} id={`group-${group.id}`} className="scroll-mt-24">
                <header className="border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                  <div className="flex items-center gap-3">
                    <span className="h-6 w-6 border-2 border-black" style={{ backgroundColor: accent }} aria-hidden />
                    <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">{group.label}</h2>
                  </div>
                  <p className="mt-3 text-sm font-bold leading-6 text-zinc-700 sm:text-base">{group.intro}</p>
                </header>

                <div className="mt-6 grid gap-4">
                  {group.items.map((item, i) => {
                    const id = `${group.id}-${i}`;
                    return (
                      <article
                        key={id}
                        id={id}
                        className="scroll-mt-24 border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:p-6"
                      >
                        <h3 className="text-base font-black text-black sm:text-lg">{item.question}</h3>
                        <p className="mt-3 text-sm font-bold leading-7 text-zinc-800 sm:text-base">
                          {item.answer}
                        </p>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        <aside className="mt-14 border-[6px] border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#A3E635]">没找到答案？</p>
          <h2 className="mt-3 text-2xl font-black uppercase sm:text-3xl">直接动手最快</h2>
          <p className="mt-3 text-sm font-bold leading-6 text-zinc-300 sm:text-base">
            协议、客户端 SDK、自托管 Platform 全部开源；最有效的「问问题」是先把最小闭环跑一次，然后在 GitHub Issue / Discussions 提具体场景。
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href="https://github.com/hejiajiudeeyu/delegated-execution-protocol/discussions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-4 border-black bg-[#A3E635] px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              GitHub Discussions
            </a>
            <a
              href="/docs/quick-start-responder"
              className="inline-flex items-center gap-2 border-4 border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              开通 Hotline
            </a>
            <a
              href="/docs/quick-start-caller"
              className="inline-flex items-center gap-2 border-4 border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              接入到 Agent
            </a>
          </div>
        </aside>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
